import React, { useState, useEffect, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  FaCheckCircle, FaTimesCircle,
  FaTruck, FaSync,
  FaCalendarAlt,
} from 'react-icons/fa';
import { useApiCall } from '../hooks/useApiCall';
import { useModal } from '../components/Modal';
import '../styles/BowserCheckups.css';
import '../styles/Statistics.css';

// ── Custom date input (matches Statistics picker style) ───────────────────────
const DateInput = React.forwardRef(({ value, onClick }, ref) => (
  <button type="button" className="stat-date-input" onClick={onClick} ref={ref}>
    <FaCalendarAlt className="stat-date-input-icon" />
    <span>{value}</span>
  </button>
));
DateInput.displayName = 'DateInput';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtTs(ts) {
  if (!ts) return '-';
  return new Date(ts * 1000).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  d.setHours(0, 0, 0, 0);
  return d;
}

function toTs(d)    { return Math.floor(new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0,  0,  0).getTime() / 1000); }
function toEndTs(d) { return Math.floor(new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59).getTime() / 1000); }

// ── Result badge ──────────────────────────────────────────────────────────────

function ResultBadge({ result }) {
  if (result === 'pass') return <span className="bc-drain bc-drain-pass"><FaCheckCircle style={{ fontSize: 9 }} /> PASS</span>;
  if (result === 'fail') return <span className="bc-drain bc-drain-fail"><FaTimesCircle style={{ fontSize: 9 }} /> FAIL</span>;
  return <span className="bc-drain bc-drain-not_done">—</span>;
}

// ── Detail modal ──────────────────────────────────────────────────────────────

function CheckupDetail({ c }) {
  return (
    <div className="bc-detail-grid">
      <div className="bc-detail-section">
        <div className="bc-detail-section-title">Overview</div>
        <div className="bc-kv"><span className="bc-kv-key">Date</span><span className="bc-kv-val">{c.check_date}</span></div>
        <div className="bc-kv"><span className="bc-kv-key">Time</span><span className="bc-kv-val">{fmtTs(c.timestamp)}</span></div>
        <div className="bc-kv"><span className="bc-kv-key">Bowser</span><span className="bc-kv-val">{c.tanker_name}</span></div>
        <div className="bc-kv"><span className="bc-kv-key">Fueler</span><span className="bc-kv-val">{c.fueler_name || '—'}</span></div>
      </div>

      <div className="bc-detail-section">
        <div className="bc-detail-section-title">Result</div>
        <div className="bc-kv"><span className="bc-kv-key">Check result</span><span className="bc-kv-val"><ResultBadge result={c.result} /></span></div>
      </div>

      {c.comments && (
        <div className="bc-detail-section full">
          <div className="bc-detail-section-title">Comments</div>
          <div className="bc-remarks-box">{c.comments}</div>
        </div>
      )}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 25;

export default function BowserCheckups() {
  const apiCall       = useApiCall();
  const { openModal } = useModal();

  const [records,      setRecords]      = useState([]);
  const [total,        setTotal]        = useState(0);
  const [page,         setPage]         = useState(1);
  const [loading,      setLoading]      = useState(true);
  const [tankers,      setTankers]      = useState([]);
  const [from,         setFrom]         = useState(daysAgo(30));
  const [to,           setTo]           = useState(new Date());
  const [tankerFilter, setTankerFilter] = useState('');
  const [resultFilter, setResultFilter] = useState('ALL');
  const [refreshLocked, setRefreshLocked] = useState(false);

  const pages = Math.ceil(total / PAGE_SIZE);

  useEffect(() => {
    apiCall('admin/bowser-checkups/form-data', 'POST').then(res => {
      if (res?.success) setTankers(res.data.tankers || []);
    });
  }, []);

  const load = useCallback((p, f, t, tk, rf) => {
    setLoading(true);
    apiCall('admin/bowser-checkups', 'POST', {
      from_ts:      toTs(f),
      to_ts:        toEndTs(t),
      page:         p,
      tanker_id:    tk || null,
      drain_result: rf !== 'ALL' ? rf : null,
    }).then(res => {
      if (res?.success) {
        setRecords(res.data.records || []);
        setTotal(res.data.total || 0);
      }
      setLoading(false);
    });
  }, [apiCall]);

  useEffect(() => { load(1, from, to, tankerFilter, resultFilter); }, []);

  useEffect(() => { setPage(1); load(1, from, to, tankerFilter, resultFilter); }, [from, to, tankerFilter, resultFilter]);

  const handleRefresh = () => {
    if (refreshLocked) return;
    setRefreshLocked(true);
    load(page, from, to, tankerFilter, resultFilter);
    setTimeout(() => setRefreshLocked(false), 5000);
  };

  const handlePage = (p) => { setPage(p); load(p, from, to, tankerFilter, resultFilter); };

  const openDetail = (id) => {
    apiCall('admin/bowser-checkups/detail', 'POST', { id }).then(res => {
      if (!res?.success) return;
      const c = res.data.record;
      openModal(
        `Bowser Check — ${c.tanker_name} · ${c.check_date}`,
        <CheckupDetail c={c} />,
        'lg'
      );
    });
  };

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Bowser Daily Checkups</h1>
          <p className="page-subtitle">UAS-OP-019 fuel control records</p>
        </div>
        <button className="btn btn-secondary" onClick={handleRefresh} disabled={refreshLocked}>
          <FaSync className={refreshLocked ? 'icon-spin' : ''} />
          {refreshLocked ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* Filters */}
      <div className="filters-row">
        <div className="search-bar bc-filter-bar">
          <DatePicker
            selected={from}
            onChange={d => d && setFrom(d)}
            selectsStart startDate={from} endDate={to}
            maxDate={to}
            dateFormat="MM/dd/yyyy"
            customInput={<DateInput />}
            popperClassName="stat-datepicker-popper"
            calendarClassName="stat-datepicker"
            popperProps={{ strategy: 'fixed' }}
          />
          <span className="bc-date-sep">→</span>
          <DatePicker
            selected={to}
            onChange={d => d && setTo(d)}
            selectsEnd startDate={from} endDate={to}
            minDate={from} maxDate={new Date()}
            dateFormat="MM/dd/yyyy"
            customInput={<DateInput />}
            popperClassName="stat-datepicker-popper"
            calendarClassName="stat-datepicker"
            popperProps={{ strategy: 'fixed' }}
          />
        </div>
        <div className="filter-btns">
          <select
            className="bc-inline-select"
            value={tankerFilter}
            onChange={e => setTankerFilter(e.target.value)}
          >
            <option value="">All bowsers</option>
            {tankers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          {[['ALL', 'All'], ['pass', 'Pass'], ['fail', 'Fail']].map(([val, lbl]) => (
            <button
              key={val}
              className={`btn btn-sm ${resultFilter === val ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setResultFilter(val)}
            >
              {lbl}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="users-table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Date</th>
              <th>Bowser</th>
              <th>Fueler</th>
              <th>Result</th>
              <th>Comments</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="table-empty">Loading…</td></tr>
            ) : records.length === 0 ? (
              <tr><td colSpan={6} className="table-empty">No checkups found for the selected filters.</td></tr>
            ) : records.map(r => (
              <tr key={r.id} className="bc-row" onClick={() => openDetail(r.id)}>
                <td className="bc-id-cell">#{r.id}</td>
                <td className="bc-date-cell">{r.check_date}</td>
                <td className="bc-meta-cell">
                  <FaTruck className="bc-cell-icon" />{r.tanker_name}
                </td>
                <td className="bc-meta-cell">{r.fueler_name || '—'}</td>
                <td><ResultBadge result={r.result} /></td>
                <td className="bc-meta-cell">{r.comments ? r.comments.slice(0, 60) + (r.comments.length > 60 ? '…' : '') : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="bc-pagination-row">
          <button className="btn btn-sm btn-secondary" disabled={page <= 1} onClick={() => handlePage(page - 1)}>Prev</button>
          <span>Page {page} of {pages} &nbsp;({total} total)</span>
          <button className="btn btn-sm btn-secondary" disabled={page >= pages} onClick={() => handlePage(page + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}
